"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";
import { ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from "lucide-react";

// Types for the table component
export interface TableColumn<T = any> {
  key: string;
  title: string | React.ReactNode;
  dataIndex?: keyof T;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  sortable?: boolean;
  sortKey?: string;
  align?: "left" | "center" | "right";
  fixed?: "left" | "right";
  render?: (value: any, record: T, index: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  className?: string;
  headerClassName?: string;
  ellipsis?: boolean;
  tooltip?: boolean;
}

export interface TableSortConfig {
  key: string;
  direction: "asc" | "desc";
}

export interface TableProps<T = any>
  extends VariantProps<typeof tableVariants> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  empty?: React.ReactNode;
  rowKey?: keyof T | ((record: T, index: number) => string | number);
  onRow?: (
    record: T,
    index: number
  ) => {
    onClick?: (event: React.MouseEvent) => void;
    onDoubleClick?: (event: React.MouseEvent) => void;
    onContextMenu?: (event: React.MouseEvent) => void;
    className?: string;
    style?: React.CSSProperties;
  };
  scroll?: {
    x?: string | number;
    y?: string | number;
  };
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  };
  // Sorting
  sortConfig?: TableSortConfig;
  onSort?: (sortConfig: TableSortConfig | null) => void;
  clientSideSort?: boolean;
  // Selection
  rowSelection?: {
    type?: "checkbox" | "radio";
    selectedRowKeys?: (string | number)[];
    onChange?: (
      selectedRowKeys: (string | number)[],
      selectedRows: T[]
    ) => void;
    onSelect?: (record: T, selected: boolean, selectedRows: T[]) => void;
    onSelectAll?: (
      selected: boolean,
      selectedRows: T[],
      changeRows: T[]
    ) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean };
  };
  // Styling
  size?: "small" | "middle" | "large";
  bordered?: boolean;
  showHeader?: boolean;
  sticky?: boolean | { offsetHeader?: number; offsetScroll?: number };
  className?: string;
  headerOnly?: boolean;
  style?: React.CSSProperties;
}

const tableVariants = cva("w-full border-collapse bg-white", {
  variants: {
    variant: {
      default: "bg-white",
      striped: "bg-white",
      bordered: "border border-gray-200 rounded-lg overflow-hidden",
    },
    size: {
      small: "[&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2 text-xs",
      middle: "[&_th]:px-4 [&_th]:py-4 [&_td]:px-4 [&_td]:py-4 text-sm",
      large: "[&_th]:px-6 [&_th]:py-5 [&_td]:px-6 [&_td]:py-5 text-base",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "middle",
  },
});

const headerVariants = cva(
  "bg-white font-medium text-gray-900 text-left border-b border-gray-200 shadow-sm relative z-10",
  {
    variants: {
      sortable: {
        true: "cursor-pointer hover:bg-gray-50 select-none transition-colors",
        false: "",
      },
    },
  }
);

const cellVariants = cva(
  "transition-colors border-b border-gray-100 last:border-b-0",
  {
    variants: {
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      },
      clickable: {
        true: "cursor-pointer",
        false: "",
      },
      rowType: {
        even: "bg-gray-50",
        odd: "bg-white",
      },
    },
    defaultVariants: {
      align: "left",
      clickable: false,
      rowType: "odd",
    },
  }
);

// Helper function to get nested value
const getNestedValue = (obj: any, path: string | number | symbol): any => {
  if (typeof path === "string") {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  }
  return obj?.[path];
};

// Helper function for client-side sorting
const sortData = <T,>(data: T[], sortConfig: TableSortConfig | null): T[] => {
  if (!sortConfig) return data;

  return [...data].sort((a, b) => {
    const aValue = getNestedValue(a, sortConfig.key);
    const bValue = getNestedValue(b, sortConfig.key);

    if (aValue === bValue) return 0;

    const comparison = aValue > bValue ? 1 : -1;
    return sortConfig.direction === "desc" ? -comparison : comparison;
  });
};

export function Table<T = any>({
  columns,
  data,
  loading = false,
  empty,
  rowKey = "id" as keyof T,
  onRow,
  scroll,
  pagination,
  sortConfig,
  onSort,
  clientSideSort = false,
  rowSelection,
  size = "middle",
  bordered = false,
  showHeader = true,
  sticky = false,
  variant = "default",
  className,
  headerOnly = false,
  style,
  ...props
}: TableProps<T>) {
  const [internalSortConfig, setInternalSortConfig] =
    React.useState<TableSortConfig | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<
    (string | number)[]
  >(rowSelection?.selectedRowKeys || []);

  const currentSortConfig = sortConfig || internalSortConfig;
  const sortedData = clientSideSort ? sortData(data, currentSortConfig) : data;

  // Handle sorting
  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable) return;

    const sortKey = column.sortKey || column.key;
    let newSortConfig: TableSortConfig | null = null;

    if (currentSortConfig?.key === sortKey) {
      // Just clear sort when clicking the same column again
      newSortConfig = null; // Clear sort
    } else {
      // New sort - always ascending
      newSortConfig = { key: sortKey, direction: "asc" };
    }

    if (onSort) {
      onSort(newSortConfig);
    } else {
      setInternalSortConfig(newSortConfig);
    }
  };

  // Handle row selection
  const handleRowSelect = (record: T, index: number, selected: boolean) => {
    const key =
      typeof rowKey === "function"
        ? rowKey(record, index)
        : getNestedValue(record, rowKey);
    let newSelectedKeys: (string | number)[];

    if (rowSelection?.type === "radio") {
      newSelectedKeys = selected ? [key] : [];
    } else {
      newSelectedKeys = selected
        ? [...selectedRowKeys, key]
        : selectedRowKeys.filter((k) => k !== key);
    }

    setSelectedRowKeys(newSelectedKeys);
    const selectedRows = sortedData.filter((item, idx) => {
      const itemKey =
        typeof rowKey === "function"
          ? rowKey(item, idx)
          : getNestedValue(item, rowKey);
      return newSelectedKeys.includes(itemKey);
    });

    rowSelection?.onChange?.(newSelectedKeys, selectedRows);
    rowSelection?.onSelect?.(record, selected, selectedRows);
  };

  // Handle select all
  const handleSelectAll = (selected: boolean) => {
    const allKeys = sortedData.map((item, index) =>
      typeof rowKey === "function"
        ? rowKey(item, index)
        : getNestedValue(item, rowKey)
    );
    const newSelectedKeys = selected ? allKeys : [];

    setSelectedRowKeys(newSelectedKeys);
    const selectedRows = selected ? sortedData : [];

    rowSelection?.onChange?.(newSelectedKeys, selectedRows);
    rowSelection?.onSelectAll?.(selected, selectedRows, sortedData);
  };

  // Render sort icon
  const renderSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null;

    const sortKey = column.sortKey || column.key;
    const isActive = currentSortConfig?.key === sortKey;

    if (isActive) {
      return currentSortConfig?.direction === "asc" ? (
        <ChevronUp className="ml-0 h-[24px] w-[24px] shrink-0 text-borderBlack" />
      ) : (
        <ChevronDown className="ml-0 h-[24px] w-[24px] shrink-0 text-borderBlack" />
      );
    }

    return (
      <ChevronsUpDown className="ml-0 h-[24px] w-[24px] shrink-0 text-borderBlack" />
    );
  };

  // Render cell content
  const renderCell = (column: TableColumn<T>, record: T, index: number) => {
    const value = column.dataIndex
      ? getNestedValue(record, column.dataIndex)
      : record;

    if (column.render) {
      return column.render(value, record, index);
    }

    if (column.ellipsis && typeof value === "string") {
      return (
        <div
          className="text-label1-d text-borderBlack"
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxHeight: '4.5em', // Fallback for browsers that don't support -webkit-line-clamp
            lineHeight: '1.5em'
          }}
          title={column.tooltip ? value : value}
        >
          {value}
        </div>
      );
    }

    return value;
  };

  // Get row props
  const getRowProps = (record: T, index: number) => {
    const baseProps = onRow?.(record, index) || {};
    const key =
      typeof rowKey === "function"
        ? rowKey(record, index)
        : getNestedValue(record, rowKey);
    const isSelected = selectedRowKeys.includes(key);

    return {
      ...baseProps,
      className: cn(
        "transition-colors",
        isSelected && "bg-blue-50",
        baseProps.className
      ),
    };
  };

  const tableElement = (
    <div className="overflow-x-auto w-full">
      <table
        className={cn("w-full border-collapse bg-white", className)}
        style={style}
        {...props}
      >
        {showHeader && (
          // ${sticky ? "sticky top-0 z-20" : ""}
          <thead
            className={`h-[80px] bg-[#F6F7FA] relative shadow-[5px_17px_30px_0px_#1F1F1F17] 
              `}
          >
            <tr>
              {rowSelection && (
                <th className={cn(headerVariants({ sortable: false }), "w-12")}>
                  {rowSelection.type !== "radio" && (
                    <input
                      type="checkbox"
                      checked={
                        selectedRowKeys.length === sortedData.length &&
                        sortedData.length > 0
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "bg-white text-[16px] font-semibold text-[#1F1F1F] text-left border-b border-gray-200 shadow-sm",
                    column.sortable &&
                      "cursor-pointer hover:bg-gray-50 select-none transition-colors",
                    column.headerClassName,
                    "py-[28px] px-[16px] bg-[#F6F7FA]",
                    column.fixed && "sticky z-10",
                    column.fixed === "right" && "right-0",
                    column.fixed === "left" && "left-0"
                  )}
                  style={{
                    width: column.width,
                    minWidth:
                      column.sortable && column.minWidth != null
                        ? Number(column.minWidth) + 28
                        : column.minWidth,
                    maxWidth:
                      column.sortable && column.maxWidth != null
                        ? Number(column.maxWidth) + 28
                        : column.maxWidth,
                    textAlign: column.align,
                    lineHeight: "22px",
                  }}
                  onClick={() => handleSort(column)}
                >
                  <span className="flex max-w-full items-start gap-1">
                    <span className="min-w-0 whitespace-normal break-words">
                      {column.headerRender ? column.headerRender() : column.title}
                    </span>
                    {renderSortIcon(column)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
        )}
        {!headerOnly && (
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (rowSelection ? 1 : 0)}
                  className="text-center py-8"
                >
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Loading...
                  </div>
                </td>
              </tr>
            ) : sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (rowSelection ? 1 : 0)}
                  className="text-center py-8 text-gray-500"
                >
                  {empty || "No data available"}
                </td>
              </tr>
            ) : (
              sortedData.map((record, index) => {
                const rowProps = getRowProps(record, index);
                const key =
                  typeof rowKey === "function"
                    ? rowKey(record, index)
                    : getNestedValue(record, rowKey);
                const isSelected = selectedRowKeys.includes(key);
                const isEvenRow = index % 2 === 0;

                return (
                  <tr
                    key={key}
                    {...rowProps}
                    className={cn(
                      "transition-colors hover:bg-blue-50",
                      isEvenRow
                        ? "bg-[#F9F9F9]"
                        : "bg-white border-b border-[#eee]",
                      isSelected && "!bg-blue-100",
                      rowProps.className
                    )}
                  >
                    {rowSelection && (
                      <td className={cn("px-4 py-4 border-b border-gray-100")}>
                        <input
                          type={rowSelection.type || "checkbox"}
                          checked={isSelected}
                          onChange={(e) =>
                            handleRowSelect(record, index, e.target.checked)
                          }
                          disabled={
                            rowSelection.getCheckboxProps?.(record)?.disabled
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          "border-b border-gray-100 transition-colors py-[28px] px-[16px] text-[16px] font-medium text-[#1F1F1F]",
                          column.align === "center" && "text-center",
                          column.align === "right" && "text-right",
                          rowProps.onClick && "cursor-pointer",
                          column.className,
                          isEvenRow
                            ? "bg-[#F9F9F9]"
                            : "bg-white border-b border-[#eeeeee]",
                          column.fixed && "sticky z-10",
                          column.fixed === "right" && "right-0",
                          column.fixed === "left" && "left-0"
                        )}
                        style={{
                          width: column.width,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {renderCell(column, record, index)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        )}
      </table>
    </div>
  );

  return tableElement;
}

// Export additional components for advanced usage
export const TableColumn = <T,>(props: TableColumn<T>) => props;
export const TableColumnGroup = ({ children }: { children: React.ReactNode }) =>
  children;
